import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {FORKYGRAM_CAPTION_LINE_HEIGHT} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {ForkygramPostAvatarType} from 'types'
import {
  getBorder,
  getCollabMemberId,
  showModalLimitedAccess,
  showModalPortalGuest,
  showModalReprocessKyc,
  showSnackbar,
  useDidMount,
  useHistory,
} from 'utils'
import {Icon, Image, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {ForkygramTemplatePostProfileAvatarProps} from './ForkygramTemplatePostProfileProps'

interface StyledAvatarContainerProps {
  isLive: boolean
  size: ForkygramPostAvatarType
}

interface StyledFollowButtonProps {
  size: ForkygramPostAvatarType
}

const StyledContainer = styled.div<StyledAvatarContainerProps>`
  ${({theme, isLive, size}) => ({
    border: getBorder(
      size === 'compact' ? 1.5 : 1,
      'solid',
      isLive ? theme.fotoyu_logo_1 : theme.white_1,
    ),
  })}
  position: relative;
  border-radius: ${convertUnit(4)};
  height: ${convertUnit(48)};
  aspect-ratio: 0.75;
  cursor: pointer;
  background-color: ${({theme}) => theme.black};
`

const StyledLiveContainer = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertUnit(1)};
  padding: 0 ${convertUnit(2)};
  background-color: ${({theme}) => theme.fotoyu_logo_1};
`

const StyledAvatar = styled(Image)`
  position: relative;
  object-fit: contain;
  aspect-ratio: 0.75;
  height: ${convertUnit(48)};
  border-radius: ${convertUnit(2.5)};
`

const StyledFollowButton = styled.div<StyledFollowButtonProps>`
  ${({size}) => ({
    top: convertUnit(size === 'compact' ? -8 : 4),
    right: convertUnit(size === 'compact' ? -1 : -10),
  })}
  position: absolute;
  width: fit-content;
  background-color: ${({theme}) => theme.white_1};
  border-radius: ${convertUnit(4)};
  padding: ${convertUnit(3)};
  padding-top: 0;
  cursor: pointer;
  z-index: 1;
`

export default function ForkygramTemplatePostProfileAvatar({
  usernameRef,
  stateShouldUpdateGap,
  data,
  size = 'default',
  containerStyle,
  onClickAvatar,
  onFollowed,
}: ForkygramTemplatePostProfileAvatarProps) {
  const history = useHistory()
  const {translate} = useTranslation()
  const {kycStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {status: childStatus} = useSelector('parentalConsentState')
  const {access_token, id} = useSelector('user') || {}
  const [followButton, setFollowButton] = useState(true)
  const setShouldUpdateGap = stateShouldUpdateGap?.[1]

  const memberId = useMemo(() => getCollabMemberId(data), [data])

  const isCanFollow = useMemo(
    () => memberId !== id && !data.is_followed && followButton,
    [data.is_followed, followButton, id, memberId],
  )

  const handleFeatureDisabled = useCallback(() => {
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, history, kycStatus, similarStatus])

  const handleFollow = useCallback(() => {
    setFollowButton(false)
    requestData('giftshop_post_follow', {
      data: {member_id: memberId},
      useDefaultMessage: true,
      actionType: 'execute',
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          onFollowed(memberId)
        } else {
          setFollowButton(true)
          showSnackbar(translate('global:messageError', {context: status}))
        }
      },
    })
  }, [memberId, onFollowed, translate])

  const handleClickFollow = useCallback(() => {
    if (!access_token) {
      showModalPortalGuest()
      return
    }
    if (
      childStatus ||
      !(kycStatus === 'verified' || kycStatus === 'verified_check')
    ) {
      handleFeatureDisabled()
      return
    }
    handleFollow()
  }, [
    access_token,
    childStatus,
    handleFeatureDisabled,
    handleFollow,
    kycStatus,
  ])

  /*
   * @todo This only runs on mount.
   * `usernameRef` doesn't trigger updates when resizing the window,
   * so we need to figure out before considering `useDidUpdate`.
   */
  useDidMount(() => {
    if (!usernameRef) return
    if (
      isCanFollow &&
      size === 'default' &&
      usernameRef.current?.offsetHeight &&
      usernameRef.current.offsetHeight > FORKYGRAM_CAPTION_LINE_HEIGHT
    ) {
      setShouldUpdateGap?.(true)
    }
  })

  return (
    <StyledContainer size={size} isLive={data.is_live} style={containerStyle}>
      {isCanFollow && (
        <StyledFollowButton size={size} onClick={handleClickFollow}>
          <Icon type="plus" size={10} color="primary_5" />
        </StyledFollowButton>
      )}
      <StyledAvatar
        alt={data.username}
        src={data.photo}
        containerStyle={{borderRadius: 'unset'}}
        onClick={onClickAvatar}
      />
      {data.is_live && (
        <StyledLiveContainer>
          <Icon type="fg" size={convertUnit(7)} color="white_1" />
          <Paragraph fontSize="xxs" fontWeight="bold" color="white_1">
            {translate('global:live').toUpperCase()}
          </Paragraph>
        </StyledLiveContainer>
      )}
    </StyledContainer>
  )
}
